import {
  parsePhoneNumberFromString,
  AsYouType,
  CountryCode,
} from 'libphonenumber-js';
import { countries, getCountryByCode } from '../data/countries';

export interface ParsedPhone {
  /** ISO country code (e.g., 'PL') */
  countryCode: string;
  /** Dial code without + (e.g., '48') */
  dialCode: string;
  /** National number without country code (e.g., '123456789') */
  nationalNumber: string;
  /** Formatted for display (e.g., '+48 123 456 789') */
  formatted: string;
  /** Raw E.164 format (e.g., '+48123456789') */
  raw: string;
}

/**
 * Parse a phone number string into components
 * Handles various formats: E.164, national with country code, etc.
 */
export function parsePhone(phone: string): ParsedPhone | null {
  if (!phone || phone.trim() === '') {
    return null;
  }

  // Ensure phone starts with + for parsing
  const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`;

  try {
    const parsed = parsePhoneNumberFromString(phoneWithPlus);

    if (parsed && parsed.country) {
      const country = getCountryByCode(parsed.country);
      if (country) {
        return {
          countryCode: parsed.country,
          dialCode: country.dialCode,
          nationalNumber: parsed.nationalNumber || '',
          formatted: parsed.formatInternational(),
          raw: parsed.format('E.164'),
        };
      }
    }

    // If libphonenumber couldn't parse, try manual extraction
    // This handles edge cases where the number is valid but country detection fails
    const cleanNumber = phone.replace(/\D/g, '');

    // Try to match dial codes from longest to shortest
    const sortedCountries = [...countries].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );

    for (const country of sortedCountries) {
      if (cleanNumber.startsWith(country.dialCode)) {
        const nationalNumber = cleanNumber.slice(country.dialCode.length);
        return {
          countryCode: country.code,
          dialCode: country.dialCode,
          nationalNumber,
          formatted: `+${country.dialCode} ${nationalNumber}`,
          raw: `+${cleanNumber}`,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Combine dial code and national number into E.164 format
 */
export function formatE164(dialCode: string, nationalNumber: string): string {
  const cleanDialCode = dialCode.replace(/\D/g, '');
  const cleanNational = nationalNumber.replace(/\D/g, '');

  if (!cleanDialCode || !cleanNational) {
    return '';
  }

  return `+${cleanDialCode}${cleanNational}`;
}

/**
 * Format a phone number for display
 * Returns formatted string like '+48 123 456 789'
 */
export function formatDisplay(phone: string): string {
  if (!phone || phone.trim() === '') {
    return '';
  }

  const parsed = parsePhone(phone);
  if (parsed) {
    return parsed.formatted;
  }

  // Fallback: return with + if not already present
  return phone.startsWith('+') ? phone : `+${phone}`;
}

/**
 * Format phone number as user types (for live formatting in input)
 */
export function formatAsYouType(
  phone: string,
  countryCode: string
): string {
  if (!phone) return '';

  try {
    const formatter = new AsYouType(countryCode as CountryCode);
    return formatter.input(phone);
  } catch {
    return phone;
  }
}

/**
 * Validate if a phone number is valid
 */
export function isValidPhone(phone: string): boolean {
  if (!phone || phone.trim() === '') {
    return false;
  }

  const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`;

  try {
    const parsed = parsePhoneNumberFromString(phoneWithPlus);
    return parsed?.isValid() ?? false;
  } catch {
    return false;
  }
}

/**
 * Get just the national number from a full phone number
 */
export function getNationalNumber(phone: string): string {
  const parsed = parsePhone(phone);
  return parsed?.nationalNumber || '';
}

/**
 * Detect country from a phone number
 */
export function detectCountry(phone: string): string | null {
  const parsed = parsePhone(phone);
  return parsed?.countryCode || null;
}
